/*
 * Grain Buds V2 cart drawer additions:
 * 1. Keeps the "Complete Your Routine" upsell strip filled in — it's a
 *    separate fetch (Shopify's product-recommendations endpoint needs its
 *    own request per product_id), refetched via MutationObserver whenever
 *    #CartDrawer's markup gets swapped by Dawn's own cart-drawer.js /
 *    cart.js (add, quantity change, remove all replace .drawer__inner).
 * 2. Wires the "Add" button on each recommended product AND the "Unlock
 *    Free Shipping" upsell card to /cart/add.js, then reuses cart-drawer's
 *    own renderContents() so header count, free-shipping bar and
 *    recommendations all refresh consistently. The free-shipping upsell
 *    itself is plain server-rendered Liquid (snippets/cart-drawer.liquid)
 *    that re-evaluates on every renderContents() swap, so it naturally
 *    updates its recommended product/hides itself once unlocked — no
 *    extra JS needed for that part.
 * 3. Swaps the free-shipping upsell's color <select> without a network
 *    request — each <option> carries the matching variant's price/image
 *    as data attributes, since the card only ever shows one product with
 *    color-only variants. Its native `change` event is intercepted on the
 *    CAPTURE phase (see below) before it can bubble into Dawn's
 *    <cart-drawer-items> (assets/cart.js CartItems), which listens for
 *    `change` anywhere inside it and treats event.target as a cart
 *    quantity <input> (reading .dataset.min/.max/.step). Our <select> has
 *    none of those, so that stock handler was misfiring on it and calling
 *    reportValidity() with "You can only add this item in increments of
 *    undefined" — a real bug, not ours to silence downstream, so it's
 *    stopped from propagating at the source instead.
 * 4. Keeps each cart line's minus button correctly disabled at that
 *    variant's real minimum purchase quantity (item.variant.quantity_rule.
 *    min, server-rendered in snippets/cart-drawer.liquid). Dawn's own
 *    quantity-rule input has its HTML `min` attribute hardcoded to "0"
 *    (by design, everywhere in this theme — see main-cart-items.liquid too
 *    — so decrementing to 0 removes the line), but its shared change
 *    validation (assets/cart.js CartItems.validateQuantity) still rejects
 *    any value below the REAL minimum with a "This item has a minimum of
 *    1" error instead of treating 0 as a remove — so hitting minus at
 *    qty 1 used to flash the quantity to 0, show that error, then reset,
 *    all client-side. Since the button's real `disabled` attribute now
 *    stops that click from ever reaching Dawn's stepDown()/dispatch in the
 *    first place, that broken path is never entered. A capture-phase click
 *    guard below is a defensive second layer for the brief window between
 *    a live +/- click and the next full server re-render.
 */
(function () {
  var lastFetchedProductId = null;

  function fetchRecommendations(container) {
    var productId = container.dataset.productId;
    if (!productId || productId === lastFetchedProductId) return;

    fetch(
      window.routes.product_recommendations_url +
        '?section_id=cart-drawer-recommendations&product_id=' +
        encodeURIComponent(productId) +
        '&limit=6&intent=complementary'
    )
      .then(function (response) { return response.text(); })
      .then(function (html) {
        lastFetchedProductId = productId;
        var freshContainer = document.getElementById('CartDrawer-Recommendations');
        if (freshContainer) freshContainer.innerHTML = html;
      })
      .catch(function (e) {
        console.error(e);
      });
  }

  function checkRecommendations() {
    var container = document.getElementById('CartDrawer-Recommendations');
    if (!container) return;
    fetchRecommendations(container);
  }

  document.addEventListener(
    'change',
    function (event) {
      var select = event.target.closest('#gb-fsu-color-select');
      if (!select) return;

      // Capture phase, ahead of Dawn's <cart-drawer-items> bubble-phase
      // change listener — stop it here so that stock quantity-input
      // validation logic never runs against this <select> at all (see
      // comment at the top of this file for why that mattered).
      event.stopPropagation();

      var option = select.options[select.selectedIndex];
      var card = select.closest('.gb-fsu__card');
      if (!card) return;

      var image = card.querySelector('#gb-fsu-image');
      var price = card.querySelector('#gb-fsu-price');
      var addButton = card.querySelector('#gb-fsu-add');

      if (image && option.dataset.image) image.src = option.dataset.image;
      if (price && option.dataset.price) price.textContent = option.dataset.price;
      if (addButton) {
        addButton.dataset.variantId = option.value;
        var unavailable = option.dataset.unavailable === 'true';
        addButton.disabled = unavailable;
        addButton.textContent = unavailable ? 'Sold Out' : '+ Add';
      }
    },
    true
  );

  function quantityMin(input) {
    var min = parseInt(input.dataset.min, 10);
    return isNaN(min) || min < 1 ? 1 : min;
  }

  function syncMinusDisabled(input) {
    var wrapper = input.closest('quantity-input');
    var minusButton = wrapper && wrapper.querySelector('.quantity__button[name="minus"]');
    if (!minusButton) return;

    var current = parseInt(input.value, 10);
    minusButton.disabled = !isNaN(current) && current <= quantityMin(input);
  }

  // Bubble phase is fine here — Dawn's own stepDown() already updated
  // input.value synchronously before dispatching this change event, so by
  // the time it reaches document the value is current regardless of
  // whether the debounced /cart/change.js request has resolved yet.
  document.addEventListener('change', function (event) {
    var input = event.target.closest('.gb-cd .quantity__input');
    if (!input) return;
    syncMinusDisabled(input);
  });

  // Defensive second layer (see file header, point 4): blocks Dawn's own
  // click handler on the minus button (attached directly to the button
  // itself) from ever running stepDown() while already at the real
  // minimum, in the brief window before the disabled attribute above has
  // caught up to a value that just changed.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest('.gb-cd .quantity__button[name="minus"]');
      if (!button || button.disabled) return;

      var wrapper = button.closest('quantity-input');
      var input = wrapper && wrapper.querySelector('.quantity__input');
      if (!input) return;

      var current = parseInt(input.value, 10);
      if (!isNaN(current) && current <= quantityMin(input)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    },
    true
  );

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.gb-cdr__add, .gb-fsu__add');
    if (!button || button.disabled) return;

    var variantId = button.dataset.variantId;
    if (!variantId) return;

    var cartDrawer = document.querySelector('cart-drawer');
    var originalLabel = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        sections: cartDrawer ? cartDrawer.getSectionsToRender().map(function (section) { return section.id; }) : undefined,
        sections_url: window.location.pathname
      })
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data.status) throw new Error(data.description || 'Could not add to cart');
        button.textContent = 'Added ✓';
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(data);
        }
        document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));
      })
      .catch(function () {
        button.textContent = "Couldn't add";
      })
      .finally(function () {
        setTimeout(function () {
          button.disabled = false;
          button.textContent = originalLabel;
        }, 1600);
      });
  });

  document.addEventListener('DOMContentLoaded', function () {
    var cartDrawerRoot = document.getElementById('CartDrawer');
    if (!cartDrawerRoot) return;

    var scheduled = false;
    var schedule = function () {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        checkRecommendations();
      });
    };

    new MutationObserver(schedule).observe(cartDrawerRoot, { childList: true, subtree: true });
    schedule();
  });
})();
